/**
 * Eniq AI per-user add-on pricing.
 *
 * Mirrors backend/services/eniq_pricing.go — keep the two in sync.
 *
 * Eniq AI Standard is included in the Corporate tier; on every other tier Eniq
 * is a paid per-user add-on. Upgrading Corporate to Eniq Pro costs only the
 * difference between Pro and Standard.
 *
 * Two price tables exist: with a phone anchor (Business Phone / FMC in the
 * bundle) Eniq costs 21/34 per user; sold standalone it costs 26/39.
 *
 * The legacy per-minute model is conserved — see ENIQ_PRICING_MODE in
 * config/pricingModes.js.
 */

export const ENIQ_TIER_STANDARD = 'standard';
export const ENIQ_TIER_PRO = 'pro';
export const ENIQ_TIERS = [ENIQ_TIER_STANDARD, ENIQ_TIER_PRO];

/** Package tier that includes Eniq AI Standard at no cost. */
export const ENIQ_INCLUDED_IN_TIER = 'corporate';

/**
 * Minimum billed users for Eniq AI in EVERY mode — mirrors the backend's
 * EniqMinBillableUsers. Even when the phone anchor (Business Phone / FMC)
 * carries fewer users, Eniq bills at least 5.
 */
export const ENIQ_MIN_BILLABLE_USERS = 5;

/**
 * Per-user monthly price by Eniq tier and region when the bundle contains a
 * phone anchor (Business Phone / FMC). Numbers are identical across regions;
 * only the displayed currency differs (EU renders the same number as EUR).
 */
export const ENIQ_ANCHORED_ADDON_PRICES = {
  [ENIQ_TIER_STANDARD]: { ua: 21, eu: 21, other: 21, asia: 21 },
  [ENIQ_TIER_PRO]: { ua: 34, eu: 34, other: 34, asia: 34 },
};

/**
 * Per-user monthly price when Eniq AI is sold standalone (no Business Phone /
 * FMC in the bundle) — standalone Eniq is more expensive.
 */
export const ENIQ_STANDALONE_ADDON_PRICES = {
  [ENIQ_TIER_STANDARD]: { ua: 26, eu: 26, other: 26, asia: 26 },
  [ENIQ_TIER_PRO]: { ua: 39, eu: 39, other: 39, asia: 39 },
};

export const normalizeEniqTier = (eniqTier) =>
  eniqTier === ENIQ_TIER_PRO ? ENIQ_TIER_PRO : ENIQ_TIER_STANDARD;

const priceForRegion = (eniqTier, region, hasPhoneAnchor) => {
  const table = hasPhoneAnchor ? ENIQ_ANCHORED_ADDON_PRICES : ENIQ_STANDALONE_ADDON_PRICES;
  const prices = table[eniqTier];
  if (!prices) return 0;
  return prices[region] !== undefined ? prices[region] : prices.other;
};

/**
 * Effective per-user price for an Eniq tier on a package tier.
 * Corporate includes Standard, so Standard is free and Pro costs the difference.
 * `hasPhoneAnchor` selects between the anchored (21/34) and standalone (26/39)
 * price tables; the Standard→Pro difference is +13 in both modes.
 */
export const getEniqPricePerUser = (eniqTier, packageTier, region, hasPhoneAnchor = true) => {
  const tier = normalizeEniqTier(eniqTier);
  let price = priceForRegion(tier, region, hasPhoneAnchor);

  if (packageTier === ENIQ_INCLUDED_IN_TIER) {
    if (tier === ENIQ_TIER_STANDARD) return 0;
    price -= priceForRegion(ENIQ_TIER_STANDARD, region, hasPhoneAnchor);
  }

  return price > 0 ? price : 0;
};

/** Whether Eniq is included in the package tier at no cost. */
export const isEniqIncludedInTier = (eniqTier, packageTier) =>
  packageTier === ENIQ_INCLUDED_IN_TIER && normalizeEniqTier(eniqTier) === ENIQ_TIER_STANDARD;

/**
 * Total monthly Eniq add-on price. Mirrors the backend: any positive user
 * count is billed as at least ENIQ_MIN_BILLABLE_USERS.
 */
export const getEniqAddonPrice = (users, eniqTier, packageTier, region, hasPhoneAnchor = true) => {
  const count = Number(users) || 0;
  if (count <= 0) return 0;
  const billed = Math.max(count, ENIQ_MIN_BILLABLE_USERS);
  return billed * getEniqPricePerUser(eniqTier, packageTier, region, hasPhoneAnchor);
};

/** Display name for an Eniq tier — intentionally untranslated, like the plan names. */
export const getEniqTierDisplayName = (eniqTier) =>
  normalizeEniqTier(eniqTier) === ENIQ_TIER_PRO ? 'Pro' : 'Standard';
